/**
 * @file dateHelper.js
 * @deprecated Легаси-хелпер. Использование любых функций из этого файла в Vue-компонентах
 * и в любом новом коде ЗАПРЕЩЕНО. Вся работа с датами выполняется только через composable
 * `@/composables/useMoment` (каноничный метод вывода — `formatToFriendlyDateTime`).
 * Файл оставлен исключительно для совместимости с уже существующим кодом и подлежит удалению.
 */

import { getRndInteger } from './utilsHelper'
import moment from 'moment'
import 'moment-timezone'
// import 'moment/min/locales.min'
import 'moment/dist/locale/ru'
import 'moment/dist/locale/kk'

import momentDurationFormatSetup from 'moment-duration-format'

export function fdm(number) {
	//Format Date Number
	const numberStr = number.toString()

	if (numberStr.length < 2) {
		return `0${number}`
	} else {
		return numberStr
	}
}

export function formatDateTime(date) {
	return `${fdm(date.getHours())}:${fdm(date.getMinutes())}`
}

export function formatDateRange(from, to, timezone, locale) {
	moment.locale(locale)

	const dateFrom = moment(from).tz(timezone)
	const dateTo = moment(to).tz(timezone)

	if (!from) {
		return ''
	} else {
		if (!to) {
			return dateFrom.format('DD.MM HH:mm:ss')
		} else {
			if (dateFrom.format('DD.MM') === dateTo.format('DD.MM')) {
				return (
					dateFrom.format('DD MMM') +
					' ' +
					dateFrom.format('HH:mm') +
					' / ' +
					dateTo.format('HH:mm')
				)
			} else {
				return dateFrom.format('DD MMM HH:mm') + ' / ' + dateTo.format('DD MMM HH:mm')
			}
		}
	}
}

export function formatDate(date, timezone, locale, format = 'DD.MM HH:mm:ss') {
	moment.locale(locale)
	const momentDate = moment(date).tz(timezone)
	return momentDate.format(format)
}

export function randomDate(
	[minYear, maxYear],
	[minMonth, maxMonth],
	[minDate, maxDate],
	[minHours, maxHours],
	[minMinutes, maxMinutes]
) {
	return new Date(
		getRndInteger(minYear, maxYear),
		getRndInteger(minMonth, maxMonth),
		getRndInteger(minDate, maxDate),
		getRndInteger(minHours, maxHours),
		getRndInteger(minMinutes, maxMinutes)
	).toISOString()
}

export function getDateBorders(dates, timezone, locale) {
	if (!dates.length) return ''

	moment.locale(locale)

	let dateFrom = moment(dates[0]).tz(timezone)
	let dateTo = moment(dates[0]).tz(timezone)

	for (const [index, date] of dates.entries()) {
		const dateObject = moment(date).tz(timezone)

		if (dateObject < dateFrom) dateFrom = dateObject
		if (dateObject > dateTo) dateTo = dateObject
	}

	return {
		from: dateFrom,
		to: dateTo,
	}
}

export function getPredefinedRanges() {
	const startToday = moment().startOf('day').format()
	const endToday = moment().endOf('day').format()
	return [
		{
			key: 'today',
			titleKey: 'Today',
			range: [startToday, endToday],
		},
	]
}

export function createDateHelper(timezone, locale, format = 'DD.MM HH:mm:ss') {
	return {
		formatDateRange: (from, to) => formatDateRange(from, to, timezone, locale),
		getDateBorders: (dates) => getDateBorders(dates, timezone, locale),
		formatDate: (date) => formatDate(date, timezone, locale, format),
		findLastDate: (objects, key) => findLastDate(objects, key, format),
	}
}

export function formatDateTimeToDate(dateTime, format = 'YYYY-MM-DD') {
	return moment(dateTime).format(format)
}

export function findLastDate(objects, key, format = 'DD.MM HH:mm:ss') {
	const sortedDates = objects?.sort((a, b) => moment(a[key]).diff(moment(b[key])))

	return sortedDates?.[sortedDates?.length - 1]
}

export function sortArrayStartsFromCurrentDate(array, key, format = 'YYYY-MM-DD') {
	const sortedArray = array?.sort((a, b) => moment(b[key]).diff(moment(a[key])))

	const formatDateArray = sortedArray?.map((el) => ({
		...el,
		[key]: moment(el[key]).format(format),
	}))

	return formatDateArray
}

export function parseDurationToSeconds(duration) {
	let hasDays = false
	let dayTimeSeparator = null

	if (duration.includes(' ')) {
		hasDays = true
		dayTimeSeparator = ' '
	}
	if (duration.includes('T')) {
		hasDays = true
		dayTimeSeparator = 'T'
	}
	// if (duration.includes('.')) {
	// 	hasDays = true
	// 	dayTimeSeparator = '.'
	// }

	let days = null
	let timeDuration = null

	if (hasDays) {
		const durationParts = duration.split(dayTimeSeparator)
		days = durationParts[0]
		timeDuration = durationParts[1]
	} else {
		timeDuration = duration
	}

	const timeDurationParts = timeDuration.split(':')
	const hours = timeDurationParts[0]
	const minutes = timeDurationParts[1]
	const seconds = timeDurationParts[2]

	const totalSeconds = parseInt(days || 0) * 86400 + parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds)
	return totalSeconds
}

export function secondsToDuration(total_seconds) {
	let day = 0
	let minute = 0
	let hour = 0
	let seconds = 0
	if (total_seconds >= 86400) {
		day = (total_seconds / 86400) | 0
		total_seconds = total_seconds - 86400 * day
	}
	if (total_seconds >= 3600) {
		hour = (total_seconds / 3600) | 0
		total_seconds = total_seconds - 3600 * hour
	}
	if (total_seconds >= 60) {
		minute = (total_seconds / 60) | 0
		total_seconds = total_seconds - 60 * minute
	}
	seconds = total_seconds
	let duration = ''
	if (day > 0) {
		duration += day + 'д.'
	}
	duration +=
		('0' + hour).slice(-2) + ':' + ('0' + minute).slice(-2) + ':' + ('0' + seconds).slice(-2)
	return duration
}

export function findDifferenceInDays(endDate, startDate) {
	return moment(endDate).diff(moment(startDate), 'days')
}

export function findDateInDays(startDate, days) {
	const formatDate = moment(startDate).format('DD-MM-YYYY')
	return moment(formatDate, 'DD.MM.YYYY').add(days, 'days').format('DD.MM.YYYY')
}

export function formateDate(currentDate) {
	const dateObject = new Date(currentDate)

	let day = dateObject.getDate()

	let month = dateObject.getMonth() + 1

	let year = dateObject.getFullYear()
	if (day < 10) {
    day = '0' + day;
}

if (month < 10) {
    month = '0' + month;
}
let format = `${day}.${month}.${year}`;

	return format
}

export function sortArrayStartsFromLatestDate(array, key) {
	const sortedArray = array?.sort((a, b) => moment(b[key]).diff(moment(a[key])))

	return sortedArray
}

export function getYearsRange(from = 2000 , to = new Date().getFullYear()) {
	return new Array(to - from + 1).fill().map((_, idx) => ({
		title: `${to - idx}`,
		value: to - idx,
	}))
}
